import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MakeGetRequest from "../utils/MakeGetRequest";
import makePostRequest from "../utils/makePostRequest";
import makeDeleteRequest from "../utils/makeDeleteRequest";
import SmallTitleText from "../SmallTitleText";
import FormInputField from "../FormInputField";
import PopUpModulBtn from "../PopUpModulBtn";
import RichTextEditorField from "../RichTextEditorField";

const getPlainTextFromHtml = (value = "") =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const CreateBlogPostForm = () => {
  const [postLoading, setPostLoading] = useState(false);
  const [postSuccessMsg, setPostSuccessMsg] = useState("");
  const [postError, setPostError] = useState("");
  const [contentValue, setContentValue] = useState("");
  const [selectedPostedBy, setSelectedPostedBy] = useState("");
  const [selectedPostedByType, setSelectedPostedByType] = useState("band");

  const navigate = useNavigate();
  const formRef = useRef(null);
  const titleRef = useRef(null);
  const authorRef = useRef(null);
  const typeRef = useRef(null);
  const publishRef = useRef(null);
  const tagsRef = useRef(null);

  // Get the location to see if its a new post or post edit.
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const slug = searchParams.get("slug");
  const apiRoute = slug
    ? `get_blogpost_form?slug=${slug}`
    : "get_blogpost_form";

  const {
    data: formData,
    error: formError,
    loading: formLoading,
  } = MakeGetRequest({
    route: apiRoute,
    forceToken: true,
    errorCodeRedirect: 404,
  });

  // Can only view post if it has content/title
  const postUrl = formData?.current_post.title
    ? `/music/${slug}/blog/${formData.current_post.title}-${formData.current_post.id}`
    : "";

  const normalizePostedByType = (rawType) => {
    const type = String(rawType || "").toLowerCase();

    if (["organizer", "organiser", "organization"].includes(type)) {
      return "organizer";
    }

    return "band";
  };

  const getPostedByTypeFromSelection = useCallback(
    (postedById) => {
      const selectedItem = formData?.post_as?.find(
        (item) => String(item?.id) === String(postedById),
      );

      return normalizePostedByType(
        selectedItem?.posted_by_type ||
          selectedItem?.type ||
          selectedItem?.entity_type ||
          formData?.current_post?.posted_by_type,
      );
    },
    [formData],
  );

  useEffect(() => {
    if (!formData) {
      return;
    }

    const initialPostedBy =
      formData?.current_post?.posted_by || formData?.post_as?.[0]?.id || "";

    setSelectedPostedBy(String(initialPostedBy));
    setSelectedPostedByType(getPostedByTypeFromSelection(initialPostedBy));
    setContentValue(formData?.current_post?.content || "");
  }, [formData, getPostedByTypeFromSelection]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPostError("");
    setPostSuccessMsg("");

    const contentPlainText = getPlainTextFromHtml(contentValue);
    if (!contentPlainText) {
      setPostError("Textfältet kan inte vara tomt");
      return;
    }

    if (formRef.current.checkValidity()) {
      // Create a FormData object
      const newData = new FormData();
      // Get all form elements
      const formElements = formRef.current.elements;

      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i];
        if (element.name) {
          switch (element.type) {
            case "checkbox":
              // Add checkbox value as a boolean
              newData.append(element.name, element.checked);
              break;

            case "file":
              if (element.files.length > 0) {
                // Handle file input
                const fileSizeInMB = element.files[0].size / (1024 * 1024);
                if (fileSizeInMB > 4) {
                  setPostError("Filstorleken är för stor");
                  return;
                }
                newData.append(element.name, element.files[0]);
              }
              break;

            default:
              switch (element.name) {
                case "posted_by":
                  if (element.value) {
                    const postedByValue = parseInt(element.value, 10);
                    if (!Number.isNaN(postedByValue)) {
                      newData.append(element.name, postedByValue);
                    }
                  }
                  break;

                case "tags":
                  // Validate the tags if tags has changed
                  if (formData.current_post.tags) {
                    if (
                      formData.current_post.tags.join(" ") === element.value
                    ) {
                      // Nothing has changed with the tags
                      continue;
                    }
                  }
                  if (element.value) {
                    const tagsArray = element.value.split(" ").slice(0, 6);
                    const hasLongTag = tagsArray.some((tag) => tag.length > 30);
                    if (hasLongTag) {
                      setPostError("Taggen är för lång");
                    } else {
                      const tagsString = tagsArray.join(" ");
                      newData.append(element.name, tagsString);
                    }
                  }
                  break;

                default:
                  newData.append(element.name, element.value);
                  break;
              }
              break;
          }
        }
      }

      newData.append("content", contentValue);

      newData.append("current_post_slug", slug ? slug : "noslug");

      setPostLoading(true);
      makePostRequest("save_blogpost", newData)
        .then((response) => {
          setPostLoading(false);
          if (response.error) {
            setPostError(response.error);
          } else {
            // handle successful response
            // Refreshe page if new post and handle msg
            if (slug) {
              setPostSuccessMsg("Inlägget är uppdaterat");
            } else {
              setPostSuccessMsg(
                "Inlägget är skapat, du flyttas tillbaka till din profil",
              );
              setTimeout(() => {
                navigate("/userprofile"); // Redirect after 2 seconds
              }, 2500);
            }
          }
        })
        .catch((error) => {
          // handle errors
          setPostError(formError || "Ett fel uppstod");
          setPostLoading(false);
        });
    } else {
      // form error
      formRef.current.reportValidity();
    }
  };

  const handleDeletePost = (e) => {
    e.preventDefault();
    makeDeleteRequest({
      route: `delete_post?slug=${slug}`,
      forceToken: true,
      redirectSuccessPath: "/userprofile",
    });
  };

  if (formLoading) {
    return <div>Loading form data...</div>;
  }

  if (!formLoading) {
    return (
      <>
        <div className="p-4">
          <div className="flex justify-between">
            {slug && (
              <PopUpModulBtn
                btnText="Ta bort"
                modalId="DeletePost"
                title="Radera inlägg"
                btnStyle="btn-outline"
                text="Det här kommer att ta bort inlägget och det finns ingen möjlighet att återkalla detta."
              >
                <p className="pb-2">Är du säker?</p>
                <button className="btn btn-error" onClick={handleDeletePost}>
                  Ta bort
                </button>
              </PopUpModulBtn>
            )}
          </div>
          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            {formData && (
              <>
                {formData.current_post.id ? (
                  <>
                    <SmallTitleText text="Uppdatera inlägget" />
                    <p className="mt-4">
                      Skapat: {formData.current_post.created_at}
                    </p>
                    {formData.current_post.created_at !=
                      formData.current_post.updated_at && (
                      <p>Uppdaterat: {formData.current_post.updated_at}</p>
                    )}
                  </>
                ) : (
                  <SmallTitleText text="Skriv inlägg" />
                )}
                <div className="my-4 max-w-xs flex flex-col">
                  <div className="label">
                    <span className="label-text text-base-content pr-2">
                      Publiceras av
                    </span>
                  </div>
                  <select
                    name="posted_by"
                    required
                    value={selectedPostedBy}
                    className="select select-bordered w-full max-w-xs"
                    ref={authorRef}
                    onChange={(e) => {
                      const nextPostedBy = e.target.value;
                      setSelectedPostedBy(nextPostedBy);
                      setSelectedPostedByType(
                        getPostedByTypeFromSelection(nextPostedBy),
                      );
                    }}
                  >
                    {formData.post_as?.map((item, index) => (
                      <option key={index} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="hidden"
                    name="posted_by_type"
                    value={selectedPostedByType}
                  />
                </div>
                <FormInputField
                  type="select"
                  name="post_type"
                  ref={typeRef}
                  label="Inläggstyp"
                  defaultValue={formData.current_post.post_type}
                  items={formData.post_types}
                  itemName="type"
                  required={true}
                />
                <FormInputField
                  type="text"
                  name="title"
                  ref={titleRef}
                  label="Titel på inlägget"
                  defaultValue={formData.current_post.title}
                  placeholder="Inläggets titel"
                  required={true}
                />
                <div className="flex space-x-8">
                  <FormInputField
                    type="file"
                    label="Huvudbild"
                    name="image_main"
                    bottomLabel="Bäst 1280x562px. Max 4mb"
                  />
                  {formData.current_post.image_main && (
                    <div className="h-28">
                      <p className="text-xs text-content">Nuvarande bild</p>
                      <img
                        className="object-contain w-full h-full"
                        src={formData.current_post.image_main}
                      />
                    </div>
                  )}
                </div>
                <RichTextEditorField
                  label="Text"
                  altLabel="Rubriker, länkar och listor stöds"
                  value={contentValue}
                  onChange={setContentValue}
                />
                <FormInputField
                  type="tags"
                  name="tags"
                  ref={tagsRef}
                  label="Taggar"
                  altLabel="Separeras med mellanslag"
                  defaultValue={
                    formData.current_post.tags
                      ? formData.current_post.tags.join(" ")
                      : ""
                  }
                  placeholder="Lägg till upp till 6 taggar"
                />

                <FormInputField
                  type="checkbox"
                  name="visible"
                  ref={publishRef}
                  label="Publicera nu?"
                  checked={formData.current_post.visible !== false}
                />
                <div className="flex justify-between items-end">
                  <div>
                    {postSuccessMsg && (
                      <p className="font-semibold text-success my-2">
                        {postSuccessMsg}
                      </p>
                    )}
                    {postError && (
                      <h4 className="font-semibold text-error mb-3 pl-1">
                        Fel, {postError}
                      </h4>
                    )}{" "}
                    {!postLoading ? (
                      <button className="btn btn-primary" type="submit">
                        {formData.current_post.id ? "Uppdatera" : "Skapa"}
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        type="submit"
                      >
                        {formData.current_post.id ? "Uppdatera" : "Skapa"}
                      </button>
                    )}
                  </div>
                  {postUrl && (
                    <a href={postUrl} className="btn btn-outline">
                      Visa inlägg
                    </a>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </>
    );
  }
};

export default CreateBlogPostForm;
